import { scannerUrl } from './scanner-url'
import { createApi, type SerializeQueryArgs } from '@reduxjs/toolkit/query/react'
import { createAuthBaseQuery } from './base-query'
import { API } from '@/config/constants'
import {
  TickerVerificationSchema,
  TickerSearchResultSchema,
  FieldDefinitionSchema,
  NewsArticleSchema,
  SecFilingItemSchema,
  FilingsByCategoryResponseSchema,
  FilingDiffResponseSchema,
  FmpTranscriptItemSchema,
  TranscriptUrlResponseSchema,
  TranscriptTextResponseSchema,
} from '@/types/schemas/api'
import type {
  TickerVerification,
  TickerSearchResult,
  FieldDefinition,
  NewsArticle,
  SecFilingItem,
  FilingsByCategoryResponse,
  FilingDiffResponse,
  FmpTranscriptItem,
  TranscriptTextResponse,
} from '@/types/schemas/api'
import {
  AggregateStatsResponseSchema,
  ChartAggregatesResponseSchema,
} from '@/features/ticker-stats/schemas/stats-types'
import type {
  AggregateStatsRequest,
  AggregateStatsResponse,
  RecordsRequest,
  RecordsResponse,
  ChartAggregatesRequest,
  ChartAggregatesResponse,
  HistoryTagFilter,
  NormalizedChartPoint,
} from '@/features/ticker-stats/schemas/stats-types'
import {
  SankeyBreakdownResponseSchema,
  type SankeyBreakdownRequest,
  type SankeyBreakdownResponse,
} from '@/features/history/schemas/sankey-types'
import {
  FinancialTimeSeriesResponseSchema,
  HistoryEarningsEntrySchema,
} from '@/features/key-stats/schemas/key-stats-types'
import type {
  FinancialTimeSeriesResponse,
  HistoryEarningsEntry,
} from '@/features/key-stats/schemas/key-stats-types'
import {
  PredictionFullSchema,
  PredictionStatsSchema,
} from '@/features/notebook/schemas/prediction-types'
import type { PredictionFull, PredictionStats } from '@/features/notebook/schemas/prediction-types'
import type { TreeNode } from '@/types/schemas/tree-node'
import { NoteSchema, TickerGroupSchema } from '@/features/notebook/schemas/note-types'
import type {
  Note,
  TickerGroup,
  CreateNoteRequest,
  UpdateNoteRequest,
  NoteListParams,
} from '@/features/notebook/schemas/note-types'
import {
  WatchlistProfileSchema,
  WatchlistItemResponseSchema,
  WatchlistCreatedResponseSchema,
} from '@/features/watchlist/schemas/watchlist-types'
import type {
  WatchlistProfile,
  WatchlistItemResponse,
  WatchlistCreatedResponse,
} from '@/features/watchlist/schemas/watchlist-types'
import { notifyWatchlistItemsChanged } from '@/features/watchlist/watchlist-items-changed'
import { rememberWatchlistNames } from '@/features/watchlist/watchlist-names'
import { GridRowSchema } from '@/types/schemas/grid-row'
import type { GridRow } from '@/types/schemas/grid-row'
import {
  TagProfileSchema,
  TagWithItemsSchema,
  TagItemSchema,
  TagItemMutationResponseSchema,
} from '@/features/tags/schemas/tag-types'
import type {
  TagProfile,
  TagWithItems,
  TagItem,
  TagItemMutationResponse,
} from '@/features/tags/schemas/tag-types'
import { SkillSchema, ToolRequestSchema } from '@/features/skills/schemas/skill-types'
import type {
  Skill,
  SkillCreateRequest,
  SkillUpdateRequest,
  ToolRequest,
  ToolRequestCreate,
} from '@/features/skills/schemas/skill-types'

export interface ParsePredictionResponse {
  tree: TreeNode
  evaluate_at: string
  model: string
  repair_attempts: number
  submit_at: string
}

/** One day in the History calendar heatmap: an ISO date and its match count. */
export interface CalendarHeatmapDay {
  date: string // "YYYY-MM-DD"
  count: number
}

/** Extract `.data` from the v2 response envelope `{ success, data, meta }`.
 * Exported for injected slices (see watchlist-memberships-api.ts). */
export function unwrapV2<T = unknown>(raw: unknown): T {
  if (raw != null && typeof raw === 'object' && 'success' in raw && 'data' in raw) {
    return (raw as { data: T }).data
  }
  return raw as T
}

/**
 * Extract both `data` and `meta` from the v2 envelope. Use this when the
 * caller needs envelope-side information (pagination meta, debug meta, etc.).
 * Falls through to `{data: raw, meta: undefined}` for un-enveloped bodies.
 */
function unwrapV2WithMeta<T = unknown>(
  raw: unknown,
): {
  data: T
  meta?: Record<string, unknown>
} {
  if (raw != null && typeof raw === 'object' && 'success' in raw && 'data' in raw) {
    const r = raw as { data: T; meta?: Record<string, unknown> }
    return { data: r.data, meta: r.meta }
  }
  return { data: raw as T, meta: undefined }
}

/**
 * RTK Query API for the Edge Data service.
 * Handles: ticker verification, news/SEC history, scanner data.
 * Auth: idToken
 */
/**
 * Flatten the `/v2/history/columns` envelope into a RecordsResponse. Shared by
 * the imperative `getHistoryRecords` mutation and the cached
 * `getHistoryRecordsPage` query (history-records-api.ts) so both parse the
 * envelope identically.
 */
export function transformRecordsResponse(raw: unknown): RecordsResponse {
  const envelope = raw as {
    success?: boolean
    data?: unknown
    meta?: Record<string, unknown>
  }
  const records = (envelope.data ?? unwrapV2(raw)) as Record<string, unknown>[]
  const meta = envelope.meta ?? {}
  const total = (meta.total as number) ?? 0
  const offset = (meta.offset as number) ?? 0
  const limit = (meta.limit as number) ?? 50
  const pw = meta.pattern_warning as { code?: string; message?: string } | undefined
  return {
    data: Array.isArray(records) ? records : [],
    has_more: offset + limit < total,
    total_count: total,
    ...(pw && pw.code && pw.message
      ? { pattern_warning: { code: pw.code, message: pw.message } }
      : {}),
  }
}

export interface NewsRscoreRelatedArticle {
  news_id: string
  ticker: string
  headline: string
  published_utc: string
  source: string
  story_url: string | null
  score: number
}

export const edgeDataApi = createApi({
  reducerPath: 'edgeDataApi',
  baseQuery: createAuthBaseQuery(API.data, 'access'),
  // Market-data queries refetch when the connection comes back (requires
  // setupListeners in store/index.ts). This is what lets bootstrap queries
  // like listWatchlists recover after an outage — the panels' 5s polls only
  // refresh DATA for ids they already have.
  refetchOnReconnect: true,
  tagTypes: [
    'Ticker',
    'News',
    'SecFilings',
    'HistoryStats',
    'HistoryRecords',
    'Fundamentals',
    'Predictions',
    'Notes',
    'Watchlists',
    'Calendar',
    'FmpTranscripts',
    'Skills',
    'ToolRequests',
    'LinkedNotes',
    'NoteEnrichment',
    'Tags',
    'PlannerStrategies',
    'PlannerRuns',
    'PriceAlerts',
    'AlertWindows',
  ],
  endpoints: (builder) => ({
    // ─── Ticker ───

    listTickers: builder.query<TickerSearchResult[], void>({
      query: () => '/v2/tickers',
      transformResponse: (raw) => TickerSearchResultSchema.array().parse(unwrapV2(raw)),
      keepUnusedDataFor: 3600,
    }),

    verifyTicker: builder.query<TickerVerification, string>({
      query: (symbol) => `/v2/tickers/${encodeURIComponent(symbol)}/verify`,
      transformResponse: (raw) => TickerVerificationSchema.parse(unwrapV2(raw)),
      providesTags: (_result, _error, symbol) => [{ type: 'Ticker', id: symbol }],
      keepUnusedDataFor: 3600,
    }),

    // ─── Field Definitions ───
    // Reference data — definitions for column keys shown in the Column
    // Picker tooltip. Returns null when no entry exists. The original
    // (old-site) implementation bypassed Lambda entirely with API Gateway
    // -> DynamoDB; we route through edge-financial-api for now and rely
    // on a long client-side cache. Eventually CloudFront/API Gateway
    // caching will sit in front of this route.
    getFieldDefinition: builder.query<FieldDefinition | null, string>({
      query: (field) => `/v2/field-definitions/${encodeURIComponent(field)}`,
      transformResponse: (raw) => {
        const data = unwrapV2<unknown>(raw)
        if (data == null) return null
        return FieldDefinitionSchema.parse(data)
      },
      keepUnusedDataFor: 86400,
    }),

    // ─── News & SEC History ───

    getNewsHistory: builder.query<
      NewsArticle[],
      {
        ticker?: string
        columns?: { column: string }[]
        categories?: Record<string, { flag: string; data?: string[] }>
        filters?: Record<string, unknown>
        keywords?: string[]
        count?: number
        prev?: string
        componentId?: string
      }
    >({
      query: ({
        ticker,
        columns,
        categories,
        filters,
        keywords,
        count = 50,
        prev,
        componentId,
      }) => ({
        url: '/v2/news',
        method: 'POST',
        body: {
          columns: columns ?? [{ column: 'ticker' }, { column: 'combinedHeadline' }],
          categories: categories ?? {
            PRESS_RELEASE: { flag: 'ALL' },
            OTHER_NEWS: { flag: 'ALL' },
            TOPICS: { flag: 'ALL' },
            SEC_CATEGORIES: { flag: 'ALL' },
          },
          count,
          ...(ticker && { ticker }),
          ...(keywords && keywords.length > 0 && { keywords }),
          ...(filters && Object.keys(filters).length > 0 && { filters }),
          ...(prev && { prev }),
          ...(componentId && { componentId }),
        },
      }),
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        // Route returns a bare list since 2026-08-24; tolerate the legacy
        // { data: [...] } inner wrap during deploy skew.
        const rows =
          unwrapped != null && typeof unwrapped === 'object' && 'data' in unwrapped
            ? (unwrapped as { data: unknown }).data
            : unwrapped
        if (!Array.isArray(rows)) return []
        return NewsArticleSchema.array().parse(rows)
      },
      serializeQueryArgs: (({ queryArgs }) =>
        JSON.stringify(queryArgs)) as SerializeQueryArgs<unknown>,
      providesTags: ['News'],
    }),

    getNewsRscoreRelated: builder.query<
      NewsRscoreRelatedArticle[],
      { news_id: string; ticker: string }
    >({
      query: (params) => ({ url: '/v2/news/rscore/related', params }),
      transformResponse: (raw) => {
        const rows = unwrapV2<unknown>(raw)
        return Array.isArray(rows) ? (rows as NewsRscoreRelatedArticle[]) : []
      },
    }),

    /**
     * Flat, time-ordered SEC filings feed for a ticker.
     *
     * USE WHEN: a single chronological list is what you need — keyword
     * search, infinite scroll, the "Chronological" filings view, or any
     * caller that wants every recent filing in one ordered stream.
     *
     * DON'T USE WHEN: you want the latest few filings per category bucket
     * (e.g. the "By Category" dashboard cards). A flat feed starves quiet
     * categories — if 8-Ks dominate the recent window, 10-Qs disappear
     * past the limit. Use `useGetSecFilingsByCategoryQuery` instead.
     */
    getSecFilings: builder.query<
      SecFilingItem[],
      { ticker: string; category?: string; limit?: number; offset?: number }
    >({
      query: ({ ticker, category, limit, offset }) => {
        const params = new URLSearchParams()
        if (category) params.set('category', category)
        if (limit) params.set('limit', String(limit))
        if (offset) params.set('offset', String(offset))
        const qs = params.toString()
        return `/v2/sec/filings/${encodeURIComponent(ticker)}${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => SecFilingItemSchema.array().parse(unwrapV2(raw)),
      providesTags: (_result, _error, { ticker }) => [{ type: 'SecFilings', id: ticker }],
      // Filings are real-time — never cache the response in-memory and always
      // refetch on mount. The user explicitly does not want to risk missing a
      // newly-filed document because Redux still had a stale 5-minute payload.
      keepUnusedDataFor: 0,
    }),

    /**
     * Top-N most recent SEC filings per category for a ticker. Returns
     * `{ earnings, news, dilution, proxy, ownership }`, each at most `n`
     * filings ordered by date DESC within the bucket. Computed
     * server-side via UNION ALL with per-bucket LIMITs, so each category
     * gets its own latest-N independent of overall filing volume.
     *
     * USE WHEN: rendering the "By Category" dashboard cards, where each
     * bucket needs its own latest-N regardless of how chatty other
     * categories are.
     *
     * DON'T USE WHEN: you want a flat time-ordered feed (search, scroll,
     * chronological view). This endpoint deliberately drops everything
     * past N per bucket — use `useGetSecFilingsQuery` instead.
     */
    getSecFilingsByCategory: builder.query<
      FilingsByCategoryResponse,
      { ticker: string; n?: number }
    >({
      query: ({ ticker, n }) => {
        const params = new URLSearchParams()
        if (n) params.set('n', String(n))
        const qs = params.toString()
        return `/v2/sec/filings/${encodeURIComponent(ticker)}/by-category${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => FilingsByCategoryResponseSchema.parse(unwrapV2(raw)),
      providesTags: (_result, _error, { ticker }) => [{ type: 'SecFilings', id: ticker }],
      // Real-time — see note on getSecFilings.
      keepUnusedDataFor: 0,
    }),

    getFilingDiff: builder.query<
      FilingDiffResponse,
      { ticker: string; base: string; compare: string }
    >({
      query: ({ ticker, base, compare }) => {
        const params = new URLSearchParams()
        params.set('base', base)
        params.set('compare', compare)
        return `/v2/sec/filings/${encodeURIComponent(ticker)}/diff?${params.toString()}`
      },
      transformErrorResponse: (error, meta) => ({
        ...error,
        data: {
          ...(error.data as object),
          retry_after: (meta as { response?: Response } | undefined)?.response?.headers.get(
            'Retry-After',
          ),
        },
      }),
      transformResponse: (raw) => FilingDiffResponseSchema.parse(unwrapV2(raw)),
      keepUnusedDataFor: 3600,
    }),

    getEarningsTranscripts: builder.query<SecFilingItem[], { ticker: string; limit?: number }>({
      query: ({ ticker, limit }) => {
        const params = new URLSearchParams()
        if (limit) params.set('limit', String(limit))
        const qs = params.toString()
        return `/v2/sec/transcripts/${encodeURIComponent(ticker)}${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => SecFilingItemSchema.array().parse(unwrapV2(raw)),
      keepUnusedDataFor: 3600,
    }),

    getFmpTranscripts: builder.query<
      FmpTranscriptItem[],
      {
        ticker: string
        limit?: number
        offset?: number
        fiscalYear?: number
        quarter?: number
      }
    >({
      query: ({ ticker, limit, offset, fiscalYear, quarter }) => {
        const params = new URLSearchParams()
        if (limit) params.set('limit', String(limit))
        if (offset) params.set('offset', String(offset))
        if (fiscalYear) params.set('fiscal_year', String(fiscalYear))
        if (quarter) params.set('quarter', String(quarter))
        const qs = params.toString()
        return `/v2/fmp/transcripts/${encodeURIComponent(ticker)}${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => FmpTranscriptItemSchema.array().parse(unwrapV2(raw)),
      providesTags: (_result, _error, { ticker }) => [{ type: 'FmpTranscripts', id: ticker }],
      keepUnusedDataFor: 3600,
    }),

    getFmpTranscriptContentUrl: builder.query<
      { url: string },
      { ticker: string; fiscalYear: number; quarter: number }
    >({
      query: ({ ticker, fiscalYear, quarter }) =>
        `/v2/fmp/transcripts/${encodeURIComponent(ticker)}/${fiscalYear}/${quarter}/content`,
      transformResponse: (raw) => TranscriptUrlResponseSchema.parse(unwrapV2(raw)),
      keepUnusedDataFor: 600,
    }),

    // NOTE: `getFmpTranscriptEnrichment` and `enrichFmpTranscriptNow` were
    // removed when the on-demand summary path migrated to edge-research's
    // `POST /v1/transcripts/summarize` (see `useTranscriptSummary`). That
    // route is a single get-or-generate call with caching + concurrency
    // dedup, so the FE no longer needs a separate read query and a manual
    // "Summarize now" mutation against edge-financial-api.

    getFmpTranscriptText: builder.query<
      TranscriptTextResponse,
      { ticker: string; fiscalYear: number; quarter: number }
    >({
      query: ({ ticker, fiscalYear, quarter }) =>
        `/v2/fmp/transcripts/${encodeURIComponent(ticker)}/${fiscalYear}/${quarter}/text`,
      transformResponse: (raw) => TranscriptTextResponseSchema.parse(unwrapV2(raw)),
      keepUnusedDataFor: 600,
    }),

    // ─── Scanner ───

    /**
     * POST /v2/scanner — used by the AG Grid SSRM datasource.
     * `debugMeta` mirrors `meta.__debug` (dev-only; undefined in prod).
     */
    postScannerData: builder.mutation<
      { rows: unknown[]; debugMeta?: Record<string, unknown> },
      {
        columns: { column: string }[]
        filters: unknown
        /** 'ABS' orders by absolute value, descending (OrderDirection.ABS). */
        order: { order_by: { column: string }; order: 'ASC' | 'DESC' | 'ABS' }[]
        historical_date?: string
        /** HH:MM New York time: the scan as of that minute. Needs a past historical_date. */
        historical_time?: string
        /** Max rows (backend default 50). The grid is capped at the plan's rows. */
        limit?: number
        /** Whole-market read (heatmap): the universe route, capped at 2,000 rows. */
        universe?: boolean
        /** Attach total_count (matches ignoring limit) to every row. Scanner panel only. */
        include_total?: boolean
      }
    >({
      query: ({ historical_date, historical_time, universe, ...body }) => ({
        url: scannerUrl({ historical_date, historical_time, universe }),
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => {
        const { data, meta } = unwrapV2WithMeta<unknown[]>(raw)
        const debugMeta = meta?.['__debug'] as Record<string, unknown> | undefined
        return { rows: data, debugMeta }
      },
    }),

    // ─── History / Ticker Stats ───

    /**
     * Market-wide regime aggregates, as a QUERY rather than a mutation so RTK
     * dedupes and caches them. One hour: the panel is the same reading for
     * every account and any historical date is as valid as today, so a cold
     * warehouse aggregate should be paid for once per hour, not per mount.
     *
     * Deliberately a separate endpoint from `getAggregateStats` (same URL, same
     * body shape) because that one is a mutation with no cache. The backend's
     * DynamoDB cache key for this route covers the stats SELECT, aggregates
     * included, so two aggregate lists never share an entry.
     */
    getMarketRegimeStats: builder.query<AggregateStatsResponse, AggregateStatsRequest>({
      query: (body) => ({
        url: '/v2/history/stats/market',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 3600,
      transformResponse: (raw) => AggregateStatsResponseSchema.parse(unwrapV2(raw)),
    }),

    getAggregateStats: builder.mutation<AggregateStatsResponse, AggregateStatsRequest>({
      query: (body) => ({
        url: '/v2/history/stats/market',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => AggregateStatsResponseSchema.parse(unwrapV2(raw)),
    }),

    getHistoryRecords: builder.mutation<RecordsResponse, RecordsRequest>({
      query: (body) => ({
        url: '/v2/history/columns',
        method: 'POST',
        body,
      }),
      transformResponse: transformRecordsResponse,
    }),

    // Read-only History charts endpoints modeled as queries (POST body) so RTK
    // gives us request dedupe, a 60s cache, and abort-on-unsubscribe for free.
    getChartAggregates: builder.query<ChartAggregatesResponse, ChartAggregatesRequest>({
      query: (body) => ({
        url: '/v2/history/charts/aggregate',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 60,
      // The endpoint returns either a raw array of rows OR
      // { data, has_more?, total_count? } depending on the request.
      // Normalize both shapes into the schema's object form.
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        const normalized = Array.isArray(unwrapped) ? { data: unwrapped } : unwrapped
        return ChartAggregatesResponseSchema.parse(normalized)
      },
    }),

    /**
     * Grouped aggregate rows for the Market Overwatch environment trend: one
     * row per session, carrying the full regime aggregate set. Cached for an
     * hour like the market stats it accompanies, since it scans the same
     * window.
     */
    /**
     * Qualifying-event counts per session over a wide window.
     *
     * Separate from `getCalendarHeatmap` despite the identical response shape,
     * for one reason: the cache lifetime. The calendar's 60 seconds is right
     * for a panel the user is scrubbing through; this one spans roughly 470
     * calendar days of warehouse scan to produce a number that changes once a
     * day, so it is held for an hour like the other regime queries.
     */
    getActivityCounts: builder.query<
      CalendarHeatmapDay[],
      {
        aggregates: Record<string, unknown>[]
        filters: Record<string, unknown>
        group_by: string
        adjusted?: boolean
      }
    >({
      query: (body) => ({
        url: '/v2/history/charts/aggregate',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 3600,
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        const rows = Array.isArray(unwrapped)
          ? (unwrapped as Record<string, unknown>[])
          : unwrapped && typeof unwrapped === 'object' && 'data' in unwrapped
            ? ((unwrapped as { data: Record<string, unknown>[] }).data ?? [])
            : []
        return rows
          .filter((r) => r.day0_date != null)
          .map((r) => ({ date: String(r.day0_date), count: Number(r.count ?? 0) }))
          .sort((a, b) => a.date.localeCompare(b.date))
      },
    }),

    getRegimeSeries: builder.query<
      Record<string, unknown>[],
      {
        aggregates: Record<string, unknown>[]
        filters: Record<string, unknown>
        group_by: string
        adjusted?: boolean
      }
    >({
      query: (body) => ({
        url: '/v2/history/charts/aggregate',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 3600,
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        if (Array.isArray(unwrapped)) return unwrapped as Record<string, unknown>[]
        if (unwrapped && typeof unwrapped === 'object' && 'data' in unwrapped) {
          return (unwrapped as { data: Record<string, unknown>[] }).data ?? []
        }
        return []
      },
    }),

    getSankeyBreakdown: builder.query<SankeyBreakdownResponse, SankeyBreakdownRequest>({
      query: (body) => ({
        url: '/v2/history/sankey',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 60,
      // The endpoint returns a bare array of cross-tab rows; wrap into { rows }.
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        const normalized = Array.isArray(unwrapped) ? { rows: unwrapped } : unwrapped
        return SankeyBreakdownResponseSchema.parse(normalized)
      },
    }),

    getCalendarHeatmap: builder.query<
      CalendarHeatmapDay[],
      {
        aggregates: Record<string, unknown>[]
        filters: Record<string, unknown>
        group_by: string
        adjusted?: boolean
        tag_filter?: HistoryTagFilter
      }
    >({
      query: (body) => ({
        url: '/v2/history/charts/aggregate',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 60,
      // Parse the day0_date/count rows into sorted calendar days here so the
      // consuming hook stays a thin cache wrapper.
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        const rows = Array.isArray(unwrapped)
          ? (unwrapped as Record<string, unknown>[])
          : unwrapped && typeof unwrapped === 'object' && 'data' in unwrapped
            ? ((unwrapped as { data: Record<string, unknown>[] }).data ?? [])
            : []
        return rows
          .filter((r) => r.day0_date != null)
          .map((r) => ({ date: String(r.day0_date), count: Number(r.count ?? 0) }))
          .sort((a, b) => a.date.localeCompare(b.date))
      },
    }),

    getIntradayPerformance: builder.query<
      { chart: NormalizedChartPoint[]; series_keys: string[]; truncated: boolean },
      {
        filters: Record<string, unknown>
        adjusted?: boolean
        agg?: string
        group_by?: string
        /** Grouped views: keep the most recent N periods (years for group_by=year). */
        max_periods?: number
        tag_filter?: HistoryTagFilter
      }
    >({
      query: (body) => ({
        url: '/v2/history/intraday-performance',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 60,
      transformResponse: (raw) => {
        const res = unwrapV2(raw) as {
          chart: NormalizedChartPoint[]
          series_keys?: string[]
          truncated?: boolean
        }
        return {
          chart: res.chart ?? [],
          series_keys: res.series_keys ?? [],
          truncated: res.truncated ?? false,
        }
      },
    }),

    // ─── News Document ───
    // Note: news summaries have been migrated to edgeResearchApi.summarizeNews
    // (POST {COMPANION_API}/v1/news/summarize). The old getNewsSummary (GET) and
    // generateNewsSummary (POST) endpoints on edge-financial-api are deprecated.

    getNewsDocument: builder.query<
      { url: string },
      { vendor: string; sourceHash: string; sourceUrl?: string; ticker?: string; filingId?: string }
    >({
      query: ({ vendor, sourceHash, sourceUrl, ticker, filingId }) => {
        if (vendor === 'sec_filings' && sourceUrl) {
          const cleanUrl = sourceUrl.replace('ix?doc=/', '')
          const params = new URLSearchParams({ url: cleanUrl })
          if (ticker && filingId) {
            params.set('ticker', ticker)
            params.set('filing_id', filingId)
          }
          return `/v2/sec/filings/html?${params.toString()}`
        }
        return `/v2/news/documents/${encodeURIComponent(vendor)}/${encodeURIComponent(sourceHash)}`
      },
      transformErrorResponse: (error, meta) => ({
        ...error,
        data: {
          ...(error.data as object),
          retry_after: (meta as { response?: Response } | undefined)?.response?.headers.get(
            'Retry-After',
          ),
        },
      }),
      transformResponse: (raw) => unwrapV2(raw) as { url: string },
    }),

    // ─── Key Stats ───

    postQuotes: builder.mutation<
      Record<string, Record<string, unknown>>,
      { tickers: string[]; columns: string[] }
    >({
      query: ({ tickers, columns }) => ({
        url: '/v2/quotes',
        method: 'POST',
        body: { tickers, columns },
      }),
      transformResponse: (raw) => {
        const records = unwrapV2<Record<string, unknown>[]>(raw)
        const result: Record<string, Record<string, unknown>> = {}
        for (const row of records) {
          const ticker = row.ticker as string
          if (ticker) {
            const { ticker: _, ...rest } = row
            result[ticker] = rest
          }
        }
        return result
      },
    }),

    getFundamentals: builder.query<Record<string, unknown>, string>({
      query: (ticker) => `/v2/financials/${encodeURIComponent(ticker)}/fundamentals`,
      transformResponse: (raw) => unwrapV2<Record<string, unknown>>(raw),
      providesTags: (_result, _error, ticker) => [{ type: 'Fundamentals', id: ticker }],
      keepUnusedDataFor: 300,
    }),

    getFinancialTimeSeries: builder.query<
      FinancialTimeSeriesResponse,
      { ticker: string; period?: string; limit?: number }
    >({
      query: ({ ticker, period, limit }) => {
        const params = new URLSearchParams()
        if (period) params.set('period', period)
        if (limit) params.set('limit', String(limit))
        const qs = params.toString()
        return `/v2/financials/${encodeURIComponent(ticker)}/statements-time-series${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => FinancialTimeSeriesResponseSchema.parse(unwrapV2(raw)),
      keepUnusedDataFor: 300,
    }),

    getHistoryEarnings: builder.query<HistoryEarningsEntry[], { ticker: string; limit?: number }>({
      query: ({ ticker, limit }) => {
        const params = new URLSearchParams()
        if (limit) params.set('limit', String(limit))
        const qs = params.toString()
        return `/v2/financials/${encodeURIComponent(ticker)}/earnings${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => HistoryEarningsEntrySchema.array().parse(unwrapV2(raw)),
      keepUnusedDataFor: 300,
    }),

    // ─── Predictions ───

    syncPrediction: builder.mutation<
      PredictionFull,
      {
        submission_id: string
        note_id: string
        ticker: string
        direction: 'bullish' | 'bearish'
        note: string
        original_text?: string
        timeframe?: string
        tree: TreeNode
      }
    >({
      query: (body) => ({
        url: '/v2/predictions',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => PredictionFullSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Predictions'],
    }),

    listPredictions: builder.query<PredictionFull[], { status?: string; ticker?: string } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams()
        if (params?.status) searchParams.set('status', params.status)
        if (params?.ticker) searchParams.set('ticker', params.ticker)
        const qs = searchParams.toString()
        return `/v2/predictions${qs ? `?${qs}` : ''}`
      },
      // The list endpoint returns `{ items: [...] }` — unwrap once for the
      // envelope and once for the inner items field.
      transformResponse: (raw) => {
        const unwrapped = unwrapV2<unknown>(raw)
        const items =
          unwrapped != null && typeof unwrapped === 'object' && 'items' in unwrapped
            ? (unwrapped as { items: unknown }).items
            : unwrapped
        if (!Array.isArray(items)) return []
        return PredictionFullSchema.array().parse(items)
      },
      providesTags: ['Predictions'],
    }),

    getPredictionStats: builder.query<PredictionStats, { minN?: number } | void>({
      query: (arg) => {
        const qs = arg?.minN ? `?min_n=${arg.minN}` : ''
        return `/v2/predictions/stats${qs}`
      },
      transformResponse: (raw) => PredictionStatsSchema.parse(unwrapV2(raw)),
      providesTags: ['Predictions'],
    }),

    getPrediction: builder.query<PredictionFull, string | number>({
      query: (predictionId) => `/v2/predictions/${encodeURIComponent(String(predictionId))}`,
      transformResponse: (raw) => PredictionFullSchema.parse(unwrapV2(raw)),
    }),

    parsePredictionText: builder.mutation<
      ParsePredictionResponse,
      { text: string; ticker?: string }
    >({
      query: (body) => ({
        url: '/v2/predictions/parse',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => unwrapV2<ParsePredictionResponse>(raw),
    }),

    deletePrediction: builder.mutation<void, string | number>({
      query: (predictionId) => ({
        url: `/v2/predictions/${encodeURIComponent(String(predictionId))}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Predictions', 'Notes'],
    }),

    overrideAssumption: builder.mutation<
      PredictionFull,
      { predictionId: string | number; field: string; value: string }
    >({
      query: ({ predictionId, field, value }) => ({
        url: `/v2/predictions/${encodeURIComponent(String(predictionId))}/assumptions/${encodeURIComponent(field)}`,
        method: 'PATCH',
        body: { value },
      }),
      transformResponse: (raw) => PredictionFullSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Predictions'],
    }),

    // ─── Notes ───

    createNote: builder.mutation<Note, CreateNoteRequest>({
      query: (body) => ({
        url: '/v2/notes',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => NoteSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Notes', 'LinkedNotes'],
    }),

    listNotes: builder.query<Note[], NoteListParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams()
        if (params) {
          if (params.kind) searchParams.set('kind', params.kind)
          if (params.ticker) searchParams.set('ticker', params.ticker)
          if (params.date_from) searchParams.set('date_from', params.date_from)
          if (params.date_to) searchParams.set('date_to', params.date_to)
          if (params.category) searchParams.set('category', params.category)
          if (params.sentiment) searchParams.set('sentiment', params.sentiment)
          if (params.q) searchParams.set('q', params.q)
          if (params.limit) searchParams.set('limit', String(params.limit))
          if (params.offset) searchParams.set('offset', String(params.offset))
        }
        const qs = searchParams.toString()
        return `/v2/notes${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => NoteSchema.array().parse(unwrapV2(raw)),
      providesTags: ['Notes', 'NoteEnrichment'],
    }),

    getNote: builder.query<Note, string>({
      query: (noteId) => `/v2/notes/${encodeURIComponent(noteId)}`,
      transformResponse: (raw) => NoteSchema.parse(unwrapV2(raw)),
    }),

    updateNote: builder.mutation<Note, { noteId: string } & UpdateNoteRequest>({
      query: ({ noteId, ...body }) => ({
        url: `/v2/notes/${encodeURIComponent(noteId)}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => NoteSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Notes', 'LinkedNotes'],
    }),

    deleteNote: builder.mutation<void, string>({
      query: (noteId) => ({
        url: `/v2/notes/${encodeURIComponent(noteId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notes'],
    }),

    getNoteTickers: builder.query<TickerGroup[], void>({
      query: () => '/v2/notes/tickers',
      transformResponse: (raw) => TickerGroupSchema.array().parse(unwrapV2(raw)),
      providesTags: ['Notes'],
    }),

    getNotesByTicker: builder.query<Note[], { ticker: string; limit?: number; offset?: number }>({
      query: ({ ticker, limit, offset }) => {
        const params = new URLSearchParams()
        if (limit) params.set('limit', String(limit))
        if (offset) params.set('offset', String(offset))
        const qs = params.toString()
        return `/v2/notes/by-ticker/${encodeURIComponent(ticker)}${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => NoteSchema.array().parse(unwrapV2(raw)),
      providesTags: ['Notes'],
    }),

    // ─── Calendar ───

    getCalendarEarnings: builder.query<
      Record<string, unknown>[],
      { dateFrom?: string; dateTo?: string; filters?: Record<string, unknown>; limit?: number }
    >({
      query: ({ dateFrom, dateTo, filters, limit }) => {
        const params = new URLSearchParams()
        if (dateFrom) params.set('date_from', dateFrom)
        if (dateTo) params.set('date_to', dateTo)
        if (limit) params.set('limit', String(limit))
        const qs = params.toString()
        return {
          url: `/v2/calendar/earnings${qs ? `?${qs}` : ''}`,
          method: 'POST',
          body: { filters: filters ?? {} },
        }
      },
      transformResponse: (raw) => unwrapV2<Record<string, unknown>[]>(raw) ?? [],
      // A read, despite the POST: the body carries the filter tree, which is
      // too large for a query string. Declaring it a `query` (not a
      // `mutation`) is what gives it arg-keyed caching, request dedup, `skip`,
      // and, most importantly, RTK Query's guarantee that the hook reflects
      // the LATEST requested args rather than whichever response resolved
      // last. As a mutation, fast date-range edits could land older rows on
      // top of newer ones. Matches getCalendarEconomic below.
      providesTags: ['Calendar'],
      keepUnusedDataFor: 3600,
    }),

    getCalendarEconomic: builder.query<
      Record<string, unknown>[],
      { dateFrom?: string; dateTo?: string; impact?: string; limit?: number }
    >({
      query: ({ dateFrom, dateTo, impact, limit }) => {
        const params = new URLSearchParams()
        if (dateFrom) params.set('date_from', dateFrom)
        if (dateTo) params.set('date_to', dateTo)
        if (impact) params.set('impact', impact)
        if (limit) params.set('limit', String(limit))
        const qs = params.toString()
        return `/v2/calendar/economic${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => unwrapV2<Record<string, unknown>[]>(raw) ?? [],
      providesTags: ['Calendar'],
      keepUnusedDataFor: 3600,
    }),

    getCalendarSplits: builder.query<
      Record<string, unknown>[],
      { dateFrom?: string; dateTo?: string; filters?: Record<string, unknown>; limit?: number }
    >({
      query: ({ dateFrom, dateTo, filters, limit }) => {
        const params = new URLSearchParams()
        if (dateFrom) params.set('date_from', dateFrom)
        if (dateTo) params.set('date_to', dateTo)
        if (limit) params.set('limit', String(limit))
        const qs = params.toString()
        return {
          url: `/v2/calendar/splits${qs ? `?${qs}` : ''}`,
          method: 'POST',
          body: { filters: filters ?? {} },
        }
      },
      transformResponse: (raw) => unwrapV2<Record<string, unknown>[]>(raw) ?? [],
      // See getCalendarEarnings above: a read that POSTs its filter tree.
      providesTags: ['Calendar'],
      keepUnusedDataFor: 3600,
    }),

    // ─── Watchlists ───

    listWatchlists: builder.query<WatchlistProfile[], string>({
      query: (namespace) => `/v2/watchlists/${encodeURIComponent(namespace)}`,
      transformResponse: (raw) => WatchlistProfileSchema.array().parse(unwrapV2(raw)),
      providesTags: ['Watchlists'],
      // Names for the watchlist change notice, which runs outside React.
      async onQueryStarted(_namespace, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          rememberWatchlistNames(data)
        } catch {
          // No names this time; the notice reads "that list".
        }
      },
    }),

    // Watchlist memberships live in watchlist-memberships-api.ts
    // (injectEndpoints): adding endpoints to THIS builder object currently
    // pushes tsc over its type-instantiation budget and silently degrades
    // other hooks' inference to `any` (surfaced as TS7006 in notebook
    // components). Prefer injected slices for new endpoints.

    // Lightweight symbol list for a single watchlist — just the tickers (the
    // `data` endpoint returns heavy market-data rows). Used by the Notebook
    // Watchlists rail to show a watchlist's symbols as ticker shortcuts.
    getWatchlistSymbols: builder.query<string[], { namespace: string; watchlistId: number }>({
      query: ({ namespace, watchlistId }) =>
        `/v2/watchlists/${encodeURIComponent(namespace)}/${watchlistId}/symbols`,
      transformResponse: (raw) => unwrapV2<string[]>(raw) ?? [],
      // Every watchlist mutation invalidates this tag, so the rail follows edits.
      providesTags: ['Watchlists'],
    }),

    fetchWatchlistData: builder.mutation<
      GridRow[],
      {
        namespace: string
        watchlistIds: number[]
        columns: (
          | string
          | { column: string; expression?: string; args?: unknown; label?: string }
        )[]
        // When set (YYYY-MM-DD), the backend returns EOD data as-of that day
        // from MotherDuck instead of realtime data.
        historical_date?: string
      }
    >({
      query: ({ namespace, watchlistIds, columns, historical_date }) => ({
        url: historical_date
          ? `/v2/watchlists/${encodeURIComponent(namespace)}/data?historical_date=${historical_date}`
          : `/v2/watchlists/${encodeURIComponent(namespace)}/data`,
        method: 'POST',
        body: { watchlistIds, columns },
      }),
      transformResponse: (raw) => GridRowSchema.array().parse(unwrapV2(raw)),
    }),

    createWatchlist: builder.mutation<
      WatchlistCreatedResponse,
      {
        namespace: string
        name: string
        copy_from_id?: number
        copy_from_source?: 'watchlist' | 'theme'
      }
    >({
      query: ({ namespace, name, copy_from_id, copy_from_source }) => ({
        url: `/v2/watchlists/${encodeURIComponent(namespace)}`,
        method: 'POST',
        body: {
          name,
          ...(copy_from_id != null ? { copy_from_id } : {}),
          ...(copy_from_source != null ? { copy_from_source } : {}),
        },
      }),
      transformResponse: (raw) => WatchlistCreatedResponseSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Watchlists'],
    }),

    addWatchlistTicker: builder.mutation<
      WatchlistItemResponse,
      { namespace: string; watchlistId: number; ticker: string }
    >({
      query: ({ namespace, watchlistId, ticker }) => ({
        url: `/v2/watchlists/${encodeURIComponent(namespace)}/${watchlistId}/${encodeURIComponent(ticker)}`,
        method: 'POST',
      }),
      transformResponse: (raw) => WatchlistItemResponseSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Watchlists'],
      async onQueryStarted({ watchlistId, ticker }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          notifyWatchlistItemsChanged({
            watchlistId,
            kind: 'added',
            tickers: [data.ticker ?? ticker],
          })
        } catch {
          // Failed request: nothing changed, nothing to refresh.
        }
      },
    }),

    removeWatchlistTicker: builder.mutation<
      void,
      // `ticker` only feeds the change signal (what left the list); the
      // request itself is by item id.
      { namespace: string; watchlistId: number; itemId: number; ticker?: string }
    >({
      query: ({ namespace, watchlistId, itemId }) => ({
        url: `/v2/watchlists/${encodeURIComponent(namespace)}/${watchlistId}/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Watchlists'],
      async onQueryStarted({ watchlistId, ticker }, { queryFulfilled }) {
        try {
          await queryFulfilled
          notifyWatchlistItemsChanged({
            watchlistId,
            kind: 'removed',
            tickers: ticker ? [ticker] : [],
          })
        } catch {
          // Failed request: nothing changed, nothing to refresh.
        }
      },
    }),

    reorderWatchlistItems: builder.mutation<
      void,
      { namespace: string; watchlistId: number; ordered_ids: number[] }
    >({
      query: ({ namespace, watchlistId, ordered_ids }) => ({
        url: `/v2/watchlists/${encodeURIComponent(namespace)}/${watchlistId}`,
        method: 'PATCH',
        body: { ordered_ids },
      }),
      // Order-only: refreshes the views that show the list in its own order.
      async onQueryStarted({ watchlistId }, { queryFulfilled }) {
        try {
          await queryFulfilled
          notifyWatchlistItemsChanged({ watchlistId, kind: 'reordered', tickers: [] })
        } catch {
          // Failed request: nothing changed, nothing to refresh.
        }
      },
    }),

    renameWatchlist: builder.mutation<
      void,
      { namespace: string; watchlistId: number; name: string }
    >({
      query: ({ namespace, watchlistId, name }) => ({
        url: `/v2/watchlists/${encodeURIComponent(namespace)}/rename/${watchlistId}`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['Watchlists'],
    }),

    deleteWatchlist: builder.mutation<void, { namespace: string; watchlistId: number }>({
      query: ({ namespace, watchlistId }) => ({
        url: `/v2/watchlists/${encodeURIComponent(namespace)}/${watchlistId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Watchlists'],
      // A scan that names a deleted list now resolves it to no members.
      async onQueryStarted({ watchlistId }, { queryFulfilled }) {
        try {
          await queryFulfilled
          notifyWatchlistItemsChanged({ watchlistId, kind: 'deleted', tickers: [] })
        } catch {
          // Failed request: nothing changed, nothing to refresh.
        }
      },
    }),

    // ─── Tags ───
    // Tags are user-owned labels on ticker+date items. `listTagsFull` is the
    // single-payload endpoint (all tags with all items) used for chips
    // rendering, filter options, and the Tags page — per-user tag volume is
    // small, so one fetch + client-side joins beats per-row lookups.

    listTags: builder.query<TagProfile[], void>({
      query: () => '/v2/tags',
      transformResponse: (raw) => TagProfileSchema.array().parse(unwrapV2(raw)),
      providesTags: ['Tags'],
    }),

    listTagsFull: builder.query<TagWithItems[], void>({
      query: () => '/v2/tags/full',
      transformResponse: (raw) => TagWithItemsSchema.array().parse(unwrapV2(raw)),
      providesTags: ['Tags'],
    }),

    getTagItems: builder.query<TagItem[], number>({
      query: (tagId) => `/v2/tags/${tagId}/items`,
      transformResponse: (raw) => TagItemSchema.array().parse(unwrapV2(raw)),
      providesTags: (_result, _error, tagId) => [{ type: 'Tags', id: tagId }, 'Tags'],
    }),

    createTag: builder.mutation<TagProfile, { name: string }>({
      query: ({ name }) => ({
        url: '/v2/tags',
        method: 'POST',
        body: { name },
      }),
      transformResponse: (raw) => TagProfileSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Tags'],
    }),

    renameTag: builder.mutation<void, { tagId: number; name: string }>({
      query: ({ tagId, name }) => ({
        url: `/v2/tags/${tagId}`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['Tags'],
    }),

    deleteTag: builder.mutation<void, { tagId: number }>({
      query: ({ tagId }) => ({
        url: `/v2/tags/${tagId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tags'],
    }),

    addTagItem: builder.mutation<
      TagItemMutationResponse,
      { tagId: number; ticker: string; targetDate?: string }
    >({
      query: ({ tagId, ticker, targetDate }) => ({
        url: `/v2/tags/${tagId}/items`,
        method: 'POST',
        body: { ticker, ...(targetDate ? { target_date: targetDate } : {}) },
      }),
      transformResponse: (raw) => TagItemMutationResponseSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['Tags'],
    }),

    // Partial update: only keys present in the body are written server-side.
    // `undefined` = leave unchanged; explicit `null` clears outcome/value.
    updateTagItem: builder.mutation<
      TagItemMutationResponse,
      {
        tagId: number
        itemId: number
        targetDate?: string
        outcome?: 'win' | 'loss' | 'breakeven' | null
        value?: number | null
      }
    >({
      query: ({ tagId, itemId, targetDate, outcome, value }) => ({
        url: `/v2/tags/${tagId}/items/${itemId}`,
        method: 'PATCH',
        body: {
          ...(targetDate !== undefined ? { target_date: targetDate } : {}),
          ...(outcome !== undefined ? { outcome } : {}),
          ...(value !== undefined ? { value } : {}),
        },
      }),
      transformResponse: (raw) => TagItemMutationResponseSchema.parse(unwrapV2(raw)),
      // Optimistic: patch the tags cache immediately so every consumer (the
      // items grid, folder counts, chips) shows the new outcome/value/date without
      // waiting for the round-trip; rolled back automatically on failure. The
      // 'Tags' invalidation still refetches afterwards to reconcile.
      async onQueryStarted(
        { tagId, itemId, targetDate, outcome, value },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          edgeDataApi.util.updateQueryData('listTagsFull', undefined, (draft) => {
            const item = draft.find((t) => t.id === tagId)?.items.find((i) => i.id === itemId)
            if (!item) return
            if (targetDate !== undefined) item.target_date = targetDate
            if (outcome !== undefined) item.outcome = outcome
            if (value !== undefined) item.value = value
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: ['Tags'],
    }),

    removeTagItem: builder.mutation<void, { tagId: number; itemId: number }>({
      query: ({ tagId, itemId }) => ({
        url: `/v2/tags/${tagId}/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tags'],
    }),

    reorderTagItems: builder.mutation<void, { tagId: number; ordered_ids: number[] }>({
      query: ({ tagId, ordered_ids }) => ({
        url: `/v2/tags/${tagId}/items`,
        method: 'PATCH',
        body: { ordered_ids },
      }),
      invalidatesTags: ['Tags'],
    }),

    // ─── Skills ───

    listSkills: builder.query<Skill[], void>({
      query: () => '/v2/skills',
      transformResponse: (raw) => SkillSchema.array().parse(unwrapV2(raw)),
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: 'Skills' as const, id: s.skill_id })),
              { type: 'Skills' as const, id: 'LIST' },
            ]
          : [{ type: 'Skills' as const, id: 'LIST' }],
    }),

    getSkill: builder.query<Skill, string>({
      query: (skillId) => `/v2/skills/${encodeURIComponent(skillId)}`,
      transformResponse: (raw) => SkillSchema.parse(unwrapV2(raw)),
      providesTags: (_result, _error, skillId) => [{ type: 'Skills', id: skillId }],
    }),

    createSkill: builder.mutation<Skill, SkillCreateRequest & { validate?: boolean }>({
      query: ({ validate = true, ...body }) => ({
        url: `/v2/skills${validate ? '' : '?validate=false'}`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => SkillSchema.parse(unwrapV2(raw)),
      invalidatesTags: [{ type: 'Skills', id: 'LIST' }],
    }),

    updateSkill: builder.mutation<
      Skill,
      { skillId: string; validate?: boolean } & SkillUpdateRequest
    >({
      query: ({ skillId, validate = true, ...body }) => ({
        url: `/v2/skills/${encodeURIComponent(skillId)}${validate ? '' : '?validate=false'}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => SkillSchema.parse(unwrapV2(raw)),
      invalidatesTags: (_result, _error, { skillId }) => [
        { type: 'Skills', id: skillId },
        { type: 'Skills', id: 'LIST' },
      ],
    }),

    deleteSkill: builder.mutation<void, string>({
      query: (skillId) => ({
        url: `/v2/skills/${encodeURIComponent(skillId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Skills', id: 'LIST' }],
    }),

    revalidateSkill: builder.mutation<Skill, string>({
      query: (skillId) => ({
        url: `/v2/skills/${encodeURIComponent(skillId)}/validate`,
        method: 'POST',
      }),
      transformResponse: (raw) => SkillSchema.parse(unwrapV2(raw)),
      invalidatesTags: (_result, _error, skillId) => [
        { type: 'Skills', id: skillId },
        { type: 'Skills', id: 'LIST' },
      ],
    }),

    // ─── Tool Requests (capability gap submissions) ───

    listToolRequests: builder.query<ToolRequest[], void>({
      query: () => '/v2/tool-requests',
      transformResponse: (raw) => ToolRequestSchema.array().parse(unwrapV2(raw)),
      providesTags: ['ToolRequests'],
    }),

    createToolRequest: builder.mutation<ToolRequest, ToolRequestCreate>({
      query: (body) => ({
        url: '/v2/tool-requests',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => ToolRequestSchema.parse(unwrapV2(raw)),
      invalidatesTags: ['ToolRequests'],
    }),

    // ─── Linked Notes (via $mention) ───

    listLinkedNotes: builder.query<Note[], { symbol: string; limit?: number; offset?: number }>({
      query: ({ symbol, limit, offset }) => {
        const params = new URLSearchParams()
        if (limit) params.set('limit', String(limit))
        if (offset) params.set('offset', String(offset))
        const qs = params.toString()
        return `/v2/tickers/${encodeURIComponent(symbol)}/linked-notes${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => NoteSchema.array().parse(unwrapV2(raw)),
      providesTags: (_result, _error, { symbol }) => [{ type: 'LinkedNotes', id: symbol }],
    }),

    // ─── Note Skill Actions ───

    /**
     * POST /v2/notes/skill-actions
     * Runs an AI skill against a note and returns the result as a string.
     * No cache tag — the result is inserted into the editor directly;
     * autosave handles the eventual note update.
     */
    runSkillAction: builder.mutation<
      { result: string; skill_name: string; note_id: string },
      { skill_name: string; note_id: string; args?: Record<string, unknown> }
    >({
      query: (body) => ({
        url: '/v2/notes/skill-actions',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) =>
        unwrapV2<{ result: string; skill_name: string; note_id: string }>(raw),
    }),
  }),
})

export const {
  useListTickersQuery,
  useVerifyTickerQuery,
  useLazyVerifyTickerQuery,
  useGetFieldDefinitionQuery,
  useGetNewsHistoryQuery,
  useGetNewsRscoreRelatedQuery,
  useLazyGetNewsRscoreRelatedQuery,
  useGetSecFilingsQuery,
  useGetSecFilingsByCategoryQuery,
  useGetFilingDiffQuery,
  usePostScannerDataMutation,
  useLazyGetNewsDocumentQuery,
  useGetAggregateStatsMutation,
  useGetHistoryRecordsMutation,
  useGetChartAggregatesQuery,
  useGetSankeyBreakdownQuery,
  usePostQuotesMutation,
  useGetFundamentalsQuery,
  useGetFinancialTimeSeriesQuery,
  useGetHistoryEarningsQuery,
  useSyncPredictionMutation,
  useListPredictionsQuery,
  useGetPredictionStatsQuery,
  useLazyGetPredictionQuery,
  useGetPredictionQuery,
  useParsePredictionTextMutation,
  useDeletePredictionMutation,
  useOverrideAssumptionMutation,
  useCreateNoteMutation,
  useListNotesQuery,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetNoteTickersQuery,
  useGetNotesByTickerQuery,
  useListWatchlistsQuery,
  useGetWatchlistSymbolsQuery,
  useFetchWatchlistDataMutation,
  useCreateWatchlistMutation,
  useAddWatchlistTickerMutation,
  useRemoveWatchlistTickerMutation,
  useReorderWatchlistItemsMutation,
  useRenameWatchlistMutation,
  useDeleteWatchlistMutation,
  useListTagsQuery,
  useListTagsFullQuery,
  useGetTagItemsQuery,
  useCreateTagMutation,
  useRenameTagMutation,
  useDeleteTagMutation,
  useAddTagItemMutation,
  useUpdateTagItemMutation,
  useRemoveTagItemMutation,
  useReorderTagItemsMutation,
  useGetCalendarEarningsQuery,
  useGetCalendarEconomicQuery,
  useGetCalendarSplitsQuery,
  useGetCalendarHeatmapQuery,
  useGetActivityCountsQuery,
  useGetMarketRegimeStatsQuery,
  useGetRegimeSeriesQuery,
  useGetIntradayPerformanceQuery,
  useGetEarningsTranscriptsQuery,
  useGetFmpTranscriptsQuery,
  useLazyGetFmpTranscriptContentUrlQuery,
  useGetFmpTranscriptTextQuery,
  useListSkillsQuery,
  useGetSkillQuery,
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  useRevalidateSkillMutation,
  useListToolRequestsQuery,
  useCreateToolRequestMutation,
  useListLinkedNotesQuery,
  useRunSkillActionMutation,
} = edgeDataApi
